import type { AuthConfig, CommitmentsData, MethodologyDoc, SectorDefinition } from '../types';
import type { DataSource } from './DataSource';

interface Manifest {
  version: number;
  updatedAt: string;
  sectors: string[];
}

/**
 * Reads sectors from static JSON files served alongside the app
 * (public/data/). This is the default, production-recommended source:
 *
 *   data/manifest.json          -> lists available sectors + last update
 *   data/sectors/<id>.json      -> one file per sector (figures + text)
 *
 * Updating the dashboards = replacing these files on the web server
 * (or in the mounted Docker volume). No rebuild, no redeploy.
 * Requests use `cache: 'no-cache'` so fresh files are picked up on reload.
 */
export class FileDataSource implements DataSource {
  constructor(private readonly baseUrl = 'data') {}

  async getSectors(): Promise<SectorDefinition[]> {
    const manifest = await this.fetchJson<Manifest>(`${this.baseUrl}/manifest.json`);
    const sectors = await Promise.all(
      manifest.sectors.map((id) =>
        this.fetchJson<SectorDefinition>(`${this.baseUrl}/sectors/${encodeURIComponent(id)}.json`),
      ),
    );
    return sectors.sort((a, b) => a.order - b.order);
  }

  async getSector(id: string): Promise<SectorDefinition | undefined> {
    try {
      return await this.fetchJson<SectorDefinition>(
        `${this.baseUrl}/sectors/${encodeURIComponent(id)}.json`,
      );
    } catch {
      return undefined;
    }
  }

  async getCommitments(): Promise<CommitmentsData> {
    return this.fetchJson<CommitmentsData>(`${this.baseUrl}/commitments.json`);
  }

  async getMethodology(): Promise<MethodologyDoc> {
    return this.fetchJson<MethodologyDoc>(`${this.baseUrl}/methodology.json`);
  }

  async getAuth(): Promise<AuthConfig | undefined> {
    try {
      return await this.fetchJson<AuthConfig>(`${this.baseUrl}/auth.json`);
    } catch {
      return undefined; // no auth file → gate disabled
    }
  }

  private async fetchJson<T>(url: string): Promise<T> {
    const res = await fetch(url, { cache: 'no-cache', headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`Failed to load ${url}: ${res.status} ${res.statusText}`);
    return (await res.json()) as T;
  }
}
